<template>
    <!-- IE 2026 09 08 / ODOO-… (заказ Игоря, Alt+Tab): лист видимых виджетов страницы по центру экрана —
         в стиле мобильного меню виджетов (Page.vue .mobile-widgets-list); логика и клавиши — utils/altTab.js -->
    <teleport to="body">
        <div v-if="st.open" class="alt-tab-backdrop" @mousedown.self="cancel">
            <!-- IE 2026 09 23 / ODOO-341407: i18n -->
            <div class="alt-tab-panel" role="listbox" :aria-label="t('Widgets')">
                <div class="alt-tab-title" v-uiHelp="'topbar.alttab.title'">{{ t('Widgets') }}</div>
                <!-- выбор мышью — только по РЕАЛЬНОМУ движению курсора (movementX/Y): Chrome шлёт синтетический
                     mouseenter/mousemove, когда панель появляется под неподвижным курсором, — он не должен
                     перебивать клавиатурное выделение -->
                <div class="alt-tab-rows" ref="rows" @mousemove="onMouseMove">
                    <template v-for="(it, i) in st.items" :key="it.id">
                        <!-- замечание Игоря 08.09: два блока — докированные и свободные — подписаны -->
                        <div v-if="st.captions && (i === 0 || it.group !== st.items[i - 1].group)" class="alt-tab-caption" v-uiHelp="{ key: 'topbar.alttab.caption', state: () => (it.group === 'free' ? 'free' : 'docked') }">
                            {{ it.group === 'free' ? t('Free', null, 'group') : t('Docked') }}</div><!-- IE 2026 09 24 / ODOO-341407: контекстный ключ словаря (ревизия омонимов) -->
                        <div class="alt-tab-row" :data-i="i"
                            :class="{ 'at-selected': i === st.index, 'at-current': it.id === st.currentId }"
                            role="option" :aria-selected="i === st.index"
                            @click.stop="commit(i)">
                            <!-- IE 2026 09 09 / ODOO-… (замечание Игоря): Tab ходит по истории активаций — цифра = место строки на этом пути -->
                            <span class="at-icon"><i :class="it.pi"></i>
                                <span v-if="st.history && st.items.length > 2" class="at-ord" :title="t('Order in the activity history (Tab walks it)')" v-uiHelp="'topbar.alttab.order'">{{ st.walk.indexOf(i) + 1 }}</span></span>
                            <span class="at-label" :title="it.title">{{ it.title }}</span>
                            <span v-if="it.id === st.currentId" class="at-mark" :title="t('Was active (Esc returns here)')" v-uiHelp="'topbar.alttab.mark'"><i class="pi pi-check"></i></span>
                        </div>
                    </template>
                    <!-- IE 2026 09 23 / ODOO-… (заказ Игоря: закрытые виджеты в Alt+Tab): блок Closed — виджеты страницы, которые
                         вернёт Alt+Esc (самый свежий первым). Перебор Tab и предпросмотр по этим строкам не ходят, цифр порядка нет,
                         наведение только подсвечивает; вернуть виджет можно ТОЛЬКО кликом мыши (reopen). Пустой стек — блока нет -->
                    <template v-if="st.closed.length">
                        <div class="alt-tab-caption at-caption-closed" v-uiHelp="'topbar.alttab.closed'">{{ t('Closed') }}</div>   <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <div v-for="(it, j) in st.closed" :key="'closed-' + it.id" class="alt-tab-row at-closed" :data-j="j" v-uiHelp="'topbar.alttab.closed'"
                            role="option" aria-selected="false" :title="t('Closed widget — click to reopen it')"
                            @click.stop="reopen(j)">
                            <span class="at-icon"><i :class="it.pi"></i></span>
                            <span class="at-label" :title="it.title">{{ it.title }}</span>
                        </div>
                    </template>
                </div>
                <!-- замечание Игоря 08.09: режимы поведения свободных виджетов (Widgets → Behavior) — полосой чипов
                     внизу листа, переключаются кликом мыши прямо при зажатом Alt; действуют сразу (предпросмотр).
                     В полосе только два режима (уточнение Игоря по скрину): «Free widgets always in front» и «Park» -->
                <div class="alt-tab-modes" :title="t('Free widgets behavior when a docked widget is active (also: user menu → Widgets → Behavior)')" v-uiHelp="'topbar.alttab.modes'">
                    <span class="at-modes-label">{{ t('Behavior') }}</span>
                    <span v-for="m in stripModes" :key="m.id" class="at-mode" v-uiHelp="{ key: 'topbar.alttab.mode.' + m.id, state: () => (fw.mode === m.id ? 'on' : 'off') }" :class="{ 'at-mode-on': fw.mode === m.id }"
                        :title="m.title" @click.stop="setMode(m.id)">{{ m.short }}</span>
                </div>
                <!-- IE 2026 09 09 / ODOO-…: подсказка по режиму хода (история активаций / строки списка) -->
                <div v-if="st.history" class="alt-tab-hint" v-uiHelp="'topbar.alttab.hint'">{{ t('Tab — back through recently active') }} &nbsp;·&nbsp; {{ t('Shift+Tab — forward') }} &nbsp;·&nbsp; {{ t('release Alt — switch') }} &nbsp;·&nbsp; {{ t('Esc — cancel') }}</div>
                <div v-else class="alt-tab-hint">{{ t('Tab — up') }} &nbsp;·&nbsp; {{ t('Shift+Tab — down') }} &nbsp;·&nbsp; {{ t('release Alt — switch') }} &nbsp;·&nbsp; {{ t('Esc — cancel') }}</div>
                <!-- IE 2026 09 23 / ODOO-… (заказ Игоря: закрытые виджеты в Alt+Tab) -->
                <div v-if="st.closed.length" class="alt-tab-hint at-hint-closed">{{ t('Closed — click to reopen (Tab skips them)') }} &nbsp;·&nbsp; {{ t('Alt+Esc — reopen the last closed') }}</div>   <!-- IE 2026 09 23 / ODOO-341407: i18n -->
            </div>
        </div>
    </teleport>
</template>

<script>
import altTab from '@/utils/altTab'
import freeWidgets, { FW_MODES } from '@/utils/freeWidgets'

export default {
    name: 'AltTabSwitcher',
    data() {
        return { st: altTab.state, fw: freeWidgets.state, stripModes: FW_MODES.filter(m => m.strip) }
    },
    watch: {
        'st.index'() { this.scrollToSelected() },
        'st.open'(v) { if (v) this.scrollToSelected() },
    },
    methods: {
        scrollToSelected() {
            this.$nextTick(() => this.$refs.rows?.querySelector('.at-selected')?.scrollIntoView?.({ block: 'nearest' }))
        },
        onMouseMove(e) {
            if (!(e.movementX || e.movementY)) return
            const row = e.target?.closest?.('.alt-tab-row')
            if (row?.classList.contains('at-closed')) return   // IE 2026 09 23 / ODOO-… (заказ Игоря: закрытые виджеты в Alt+Tab): предпросмотра у закрытого нет
            if (row) altTab.select(Number(row.dataset.i))
        },
        commit(i) { altTab.commit(i) },
        reopen(j) { altTab.reopen(j) },   // IE 2026 09 23 / ODOO-… (заказ Игоря: закрытые виджеты в Alt+Tab)
        cancel() { altTab.cancel() },
        setMode(id) { freeWidgets.setMode(id) },
    },
}
</script>

<style>
/* IE 2026 09 08 / ODOO-… (Alt+Tab): оверлей телепортирован в body — стили глобальные, префикс alt-tab-;
   палитра и геометрия строк = мобильное меню виджетов (Page.vue .mobile-widgets-row) */
.alt-tab-backdrop {
    position: fixed;
    inset: 0;
    z-index: 2500;
    display: flex;
    align-items: center;
    justify-content: center;
    /* замечание Игоря 08.09: фон при переборе не затемнять — подложка только ловит клик мимо листа,
       «стекло» остаётся у самой панели (как у мобильного .mobile-widgets-backdrop) */
    background: transparent;
}
.alt-tab-panel {
    background: rgba(209, 216, 249, .55);
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    border-radius: 16px;
    padding: 12px 14px 10px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, .25);
    min-width: 340px;
    max-width: min(640px, 90vw);
    max-height: 80vh;
    display: flex;
    flex-direction: column;
}
.alt-tab-title {
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--text-color-secondary, #6c757d);
    opacity: .75;
    padding: 2px 6px 4px 6px;   /* IE 2026 09 09 / ODOO-…: было 10px снизу — компенсация отступа .alt-tab-rows */
}
.alt-tab-rows {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 2px 2px;   /* IE 2026 09 09 / ODOO-…: сверху место под цифру порядка, выступающую над первой иконкой */
}
.alt-tab-row {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
}
.alt-tab-caption {
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--text-color-secondary, #6c757d);
    opacity: .7;
    padding: 6px 6px 0 8px;
    border-top: 1px solid rgba(100, 118, 239, .25);
}
.alt-tab-caption:first-child { border-top: none; padding-top: 0; }
.alt-tab-row .at-icon {
    width: 45px;
    height: 52px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #6476ef3e;
    color: var(--primary-color-text, #fff);
    border-radius: 12px;
}
.alt-tab-row .at-icon i { font-size: 24px; }
/* IE 2026 09 09 / ODOO-…: цифра порядка по истории активаций в углу иконки */
.alt-tab-row .at-icon { position: relative; }
.alt-tab-row .at-ord {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    border-radius: 9px;
    background: #fff;
    color: var(--text-color-secondary, #495057);
    border: 1px solid rgba(100, 118, 239, .5);
    font-size: .7rem;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
}
.alt-tab-row.at-selected .at-ord { border-color: var(--cs-accent, #6477ef); color: var(--cs-accent, #6477ef); }
.alt-tab-row .at-label {
    background-color: #d1d8f9;
    color: var(--text-color-secondary, #495057);
    font-size: 1.15rem;
    line-height: 1.3;
    padding: 7px 14px;
    border-radius: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 460px;
    border: 2px solid transparent;
}
.alt-tab-row.at-selected .at-icon { background-color: var(--cs-accent, #6477ef); }
.alt-tab-row.at-selected .at-label {
    border-color: var(--cs-accent, #6477ef);
    color: var(--text-color, #212529);
}
.alt-tab-row .at-mark {
    width: 40px;
    height: 40px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #d1d8f9;
    border-radius: 12px;
    color: var(--text-color-secondary, #495057);
}
/* живой предпросмотр: выделенный виджет на время перебора выше свободных виджетов (z 900+) и режимов
   freeWidgets (1300/1350), но ниже wijmo-попапов (1500) и листа (2500); после отпускания Alt атрибут
   снимается — дальше стек по режиму Widgets → Behavior
   (атрибут, а не класс: className контейнера переписывает реактивный binding Widget.vue) */
.WIDGET[data-alt-tab-lift] { z-index: 1400 !important; }
/* полоса режимов Behavior под списком (чипы, текущий — цветом гаммы) */
.alt-tab-modes {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px 4px 0;
    border-top: 1px solid rgba(100, 118, 239, .25);
    margin-top: 8px;
}
.alt-tab-modes .at-modes-label {
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--text-color-secondary, #6c757d);
    opacity: .7;
    padding-right: 4px;
}
.alt-tab-modes .at-mode {
    padding: 4px 10px;
    border-radius: 10px;
    background: #d1d8f9;
    color: var(--text-color-secondary, #495057);
    font-size: .85rem;
    cursor: pointer;
    white-space: nowrap;
    border: 2px solid transparent;
}
.alt-tab-modes .at-mode:hover { border-color: var(--cs-accent, #6477ef); }
.alt-tab-modes .at-mode.at-mode-on {
    background: var(--cs-accent, #6477ef);
    color: #fff;
    font-weight: 600;
}
.alt-tab-hint {
    font-size: .8rem;
    color: var(--text-color-secondary, #6c757d);
    opacity: .8;
    padding: 10px 6px 0;
    white-space: nowrap;
}
/* IE 2026 09 23 / ODOO-… (заказ Игоря: закрытые виджеты в Alt+Tab): строки блока Closed приглушены и курсивом,
   без цифр порядка и отметки «был активен»; наведение — только рамка подписи (выделения/предпросмотра нет, вернуть — кликом) */
.alt-tab-row.at-closed { opacity: .55; }
.alt-tab-row.at-closed .at-label { font-style: italic; }
.alt-tab-row.at-closed:hover { opacity: .9; }
.alt-tab-row.at-closed:hover .at-label { border-color: var(--cs-accent, #6477ef); }
.alt-tab-hint.at-hint-closed { padding-top: 4px; }
</style>
